/**
 * Global AI Tools Registry
 *
 * Contains tools that should be available throughout the entire chat session.
 *
 * ## When to add tools HERE:
 * - Tool should work everywhere (navigation, global search, etc.)
 * - Tool doesn't need component-specific state
 *
 * ## When to add tools IN A COMPONENT:
 * - Tool needs access to component state/props
 * - Tool is only relevant when that component is visible
 * - See COMPONENT_TOOLS_EXAMPLE.md for examples
 *
 * @example Using global tools
 * ```tsx
 * import { useAITools } from '@/hooks/aiTools';
 *
 * function ChatPage() {
 *   useAITools(); // Registers all global tools
 *   return <CopilotChat />;
 * }
 * ```
 *
 * @example Component-specific tool
 * ```tsx
 * import { useCopilotAction } from '@copilotkit/react-core';
 *
 * function EmployeeDetailsPage({ employeeId }) {
 *   useCopilotAction({
 *     name: 'update_employee_status',
 *     description: 'Update the status of the currently viewed employee',
 *     parameters: [{ name: 'status', type: 'string', required: true }],
 *     handler: async ({ status }) => {
 *       await updateStatus(employeeId, status);
 *       return 'Status updated';
 *     }
 *   });
 *   return <div>Employee: {employeeId}</div>;
 * }
 * ```
 */

import { useNavigationTool as navigationTool } from './useNavigationTool';
import { useAddToCartTool as addToCartTool } from './useAddToCartTool';
import { useConnectAppIntegrationTool as connectAppIntegrationTool } from './useConnectAppIntegrationTool';
import { useConnectHrSystemTool as connectHrSystemTool } from './useConnectHrSystemTool';
import { useOpenSupportTicketTool as openSupportTicketTool } from './useOpenSupportTicketTool';
import { useImportEmployeesCSVTool as importEmployeesCSVTool } from './useImportEmployeesCSVTool';

export { useNavigationTool } from './useNavigationTool';
export { useAddToCartTool } from './useAddToCartTool';
export { useConnectAppIntegrationTool } from './useConnectAppIntegrationTool';
export { useConnectHrSystemTool } from './useConnectHrSystemTool';
export { useOpenSupportTicketTool } from './useOpenSupportTicketTool';
export { useImportEmployeesCSVTool } from './useImportEmployeesCSVTool';

/**
 * Registers all global AI tools with CopilotKit.
 * Call once in your chat interface to enable global tools.
 *
 * @param setupAssistantOnlyMode - When true, only setup-step-relevant tools are
 * exposed to the LLM (navigation, connect integration/HR system, import CSV) —
 * useAddToCartTool and useOpenSupportTicketTool stay registered (stable hook
 * call order) but are hidden via `available: 'disabled'`.
 * // TODO(TF-677): remove once gigawatt_setup_assistant entitlement ships and
 * // free-plan orgs get the same tool access as everyone else.
 */
export const useAITools = (setupAssistantOnlyMode = false) => {
  navigationTool();
  addToCartTool({ enabled: !setupAssistantOnlyMode });
  connectAppIntegrationTool();
  connectHrSystemTool();
  openSupportTicketTool({ enabled: !setupAssistantOnlyMode });
  importEmployeesCSVTool();
};
